'use client';

import { useEffect } from 'react';
import { Shield, Eye, Lock, Users, Database, Globe } from 'lucide-react';

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = 'Privacy Policy - AlfaGPT';
  }, []);

  return (
    <div className="max-w-4xl mx-auto py-8 px-6">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="flex justify-center mb-4">
          <Shield className="w-16 h-16 text-blue-600 dark:text-blue-400" />
        </div>
        <h1 className="text-4xl font-bold text-text-primary mb-4">Privacy Policy</h1>
        <p className="text-lg text-text-secondary max-w-2xl mx-auto">
          We are committed to protecting your privacy and ensuring the security of your personal
          information. This policy explains how we collect, use, and safeguard your data.
        </p>
        <p className="text-sm text-text-secondary dark:text-text-secondary mt-4">
          Last updated:{' '}
          {new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>
      </div>

      {/* Content */}
      <div className="prose prose-lg dark:prose-invert max-w-none">
        {/* Information We Collect */}
        <section className="mb-12">
          <div className="flex items-center mb-6">
            <Database className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-3" />
            <h2 className="text-2xl font-semibold text-text-primary">Information We Collect</h2>
          </div>

          <div className="space-y-6">
            <div className="bg-bg-main p-6 rounded-lg">
              <h3 className="text-xl font-medium text-text-primary mb-3">Personal Information</h3>
              <ul className="list-disc list-inside text-text-primary space-y-2">
                <li>Account information (name, email address, profile picture)</li>
                <li>Authentication data (encrypted passwords, OAuth tokens)</li>
                <li>Communication preferences and settings</li>
                <li>Subscription and billing information</li>
              </ul>
            </div>

            <div className="bg-bg-main p-6 rounded-lg">
              <h3 className="text-xl font-medium text-text-primary mb-3">Usage Information</h3>
              <ul className="list-disc list-inside text-text-primary space-y-2">
                <li>Chat conversations and interactions with AI models</li>
                <li>Search queries and analysis requests</li>
                <li>Feature usage patterns and preferences</li>
                <li>Device information and browser data</li>
                <li>IP addresses and location data (anonymized)</li>
              </ul>
            </div>

            <div className="bg-bg-main p-6 rounded-lg">
              <h3 className="text-xl font-medium text-text-primary mb-3">Technical Information</h3>
              <ul className="list-disc list-inside text-text-primary space-y-2">
                <li>Log files and error reports</li>
                <li>Performance metrics and analytics</li>
                <li>Cookies and similar tracking technologies</li>
                <li>API usage and integration data</li>
              </ul>
            </div>
          </div>
        </section>

        {/* How We Use Your Information */}
        <section className="mb-12">
          <div className="flex items-center mb-6">
            <Eye className="w-6 h-6 text-green-600 dark:text-green-400 mr-3" />
            <h2 className="text-2xl font-semibold text-text-primary">
              How We Use Your Information
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-green-50 bg-green-900/20 p-6 rounded-lg border border-green-200 dark:border-green-800">
              <h3 className="text-lg font-medium text-green-900 dark:text-green-100 mb-3">
                Service Provision
              </h3>
              <ul className="text-green-800 dark:text-green-200 space-y-2 text-sm">
                <li>• Provide AI-powered analysis and insights</li>
                <li>• Maintain and improve our services</li>
                <li>• Process your requests and queries</li>
                <li>• Manage your account and subscriptions</li>
              </ul>
            </div>

            <div className="bg-blue-50 bg-blue-900/20 p-6 rounded-lg border border-blue-200 dark:border-blue-800">
              <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-3">
                Communication
              </h3>
              <ul className="text-blue-800 dark:text-blue-200 space-y-2 text-sm">
                <li>• Send service updates and notifications</li>
                <li>• Respond to support requests</li>
                <li>• Share product announcements</li>
                <li>• Provide security alerts</li>
              </ul>
            </div>

            <div className="bg-purple-50 bg-purple-900/20 p-6 rounded-lg border border-purple-200 dark:border-purple-800">
              <h3 className="text-lg font-medium text-purple-900 dark:text-purple-100 mb-3">
                Improvement
              </h3>
              <ul className="text-purple-800 dark:text-purple-200 space-y-2 text-sm">
                <li>• Analyze usage patterns</li>
                <li>• Develop new features</li>
                <li>• Enhance AI model performance</li>
                <li>• Optimize user experience</li>
              </ul>
            </div>

            <div className="bg-orange-50 bg-orange-900/20 p-6 rounded-lg border border-orange-200 dark:border-orange-800">
              <h3 className="text-lg font-medium text-orange-900 dark:text-orange-100 mb-3">
                Legal Compliance
              </h3>
              <ul className="text-orange-800 dark:text-orange-200 space-y-2 text-sm">
                <li>• Comply with legal obligations</li>
                <li>• Prevent fraud and abuse</li>
                <li>• Protect user safety</li>
                <li>• Enforce terms of service</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Data Security */}
        <section className="mb-12">
          <div className="flex items-center mb-6">
            <Lock className="w-6 h-6 text-red-600 dark:text-red-400 mr-3" />
            <h2 className="text-2xl font-semibold text-text-primary">Data Security</h2>
          </div>

          <div className="bg-red-50 bg-red-900/20 p-8 rounded-lg border border-red-200 dark:border-red-800">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-medium text-red-900 dark:text-red-100 mb-4">
                  Technical Safeguards
                </h3>
                <ul className="text-red-800 dark:text-red-200 space-y-2">
                  <li>• End-to-end encryption for sensitive data</li>
                  <li>• Secure HTTPS connections</li>
                  <li>• Regular security audits and updates</li>
                  <li>• Multi-factor authentication support</li>
                  <li>• Automated backup and recovery systems</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-medium text-red-900 dark:text-red-100 mb-4">
                  Operational Security
                </h3>
                <ul className="text-red-800 dark:text-red-200 space-y-2">
                  <li>• Limited access to personal data</li>
                  <li>• Employee security training</li>
                  <li>• Incident response procedures</li>
                  <li>• Third-party security assessments</li>
                  <li>• Compliance with industry standards</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Data Sharing */}
        <section className="mb-12">
          <div className="flex items-center mb-6">
            <Users className="w-6 h-6 text-purple-600 dark:text-purple-400 mr-3" />
            <h2 className="text-2xl font-semibold text-text-primary">
              Data Sharing and Disclosure
            </h2>
          </div>

          <div className="space-y-4">
            <div className="bg-yellow-50 bg-yellow-900/20 p-6 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <h3 className="text-lg font-medium text-yellow-900 dark:text-yellow-100 mb-3">
                We DO NOT sell your personal data
              </h3>
              <p className="text-yellow-800 dark:text-yellow-200">
                Your personal information is never sold to third parties for marketing or commercial
                purposes.
              </p>
            </div>

            <div className="bg-bg-main p-6 rounded-lg">
              <h3 className="text-lg font-medium text-text-primary mb-3">
                Limited sharing occurs only when:
              </h3>
              <ul className="list-disc list-inside text-text-primary space-y-2">
                <li>Required by law or legal process</li>
                <li>Necessary to protect user safety or prevent fraud</li>
                <li>With service providers who assist in operations (under strict agreements)</li>
                <li>In case of business transfer or merger (with user notification)</li>
                <li>With your explicit consent</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Your Rights */}
        <section className="mb-12">
          <div className="flex items-center mb-6">
            <Globe className="w-6 h-6 text-indigo-600 dark:text-indigo-400 mr-3" />
            <h2 className="text-2xl font-semibold text-text-primary">Your Rights and Choices</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-indigo-50 bg-indigo-900/20 p-6 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <h3 className="text-lg font-medium text-indigo-900 dark:text-indigo-100 mb-3">
                Access & Portability
              </h3>
              <p className="text-indigo-800 dark:text-indigo-200 text-sm">
                Request a copy of your personal data in a portable format
              </p>
            </div>

            <div className="bg-indigo-50 bg-indigo-900/20 p-6 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <h3 className="text-lg font-medium text-indigo-900 dark:text-indigo-100 mb-3">
                Correction & Update
              </h3>
              <p className="text-indigo-800 dark:text-indigo-200 text-sm">
                Correct or update your personal information at any time
              </p>
            </div>

            <div className="bg-indigo-50 bg-indigo-900/20 p-6 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <h3 className="text-lg font-medium text-indigo-900 dark:text-indigo-100 mb-3">
                Deletion
              </h3>
              <p className="text-indigo-800 dark:text-indigo-200 text-sm">
                Request deletion of your account and associated data
              </p>
            </div>
          </div>

          <div className="mt-6 bg-bg-main p-6 rounded-lg">
            <h3 className="text-lg font-medium text-text-primary mb-3">
              How to Exercise Your Rights
            </h3>
            <p className="text-text-primary mb-4">
              To exercise any of these rights, please contact us through:
            </p>
            <ul className="list-disc list-inside text-text-primary space-y-1">
              <li>Email: privacy@pythra.com</li>
              <li>Settings page in your account dashboard</li>
              <li>Support ticket system</li>
            </ul>
          </div>
        </section>

        {/* Cookies and Tracking */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-text-primary mb-6">
            Cookies and Tracking Technologies
          </h2>

          <div className="bg-blue-50 bg-blue-900/20 p-6 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-blue-900 dark:text-blue-100 mb-4">
              We use cookies and similar technologies to enhance your experience:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                  Essential Cookies
                </h4>
                <p className="text-blue-800 dark:text-blue-200 text-sm">
                  Required for basic functionality, authentication, and security
                </p>
              </div>
              <div>
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                  Analytics Cookies
                </h4>
                <p className="text-blue-800 dark:text-blue-200 text-sm">
                  Help us understand usage patterns and improve our services
                </p>
              </div>
            </div>
            <p className="text-blue-800 dark:text-blue-200 text-sm mt-4">
              You can manage cookie preferences through your browser settings or our cookie consent
              banner.
            </p>
          </div>
        </section>

        {/* International Transfers */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-text-primary mb-6">
            International Data Transfers
          </h2>

          <div className="bg-bg-main p-6 rounded-lg">
            <p className="text-text-primary mb-4">
              Your data may be processed in countries other than your own. We ensure appropriate
              safeguards are in place:
            </p>
            <ul className="list-disc list-inside text-text-primary space-y-2">
              <li>Standard Contractual Clauses (SCCs) for EU data transfers</li>
              <li>Adequacy decisions where applicable</li>
              <li>Certification schemes and codes of conduct</li>
              <li>Regular assessment of transfer mechanisms</li>
            </ul>
          </div>
        </section>

        {/* Data Retention */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-text-primary mb-6">Data Retention</h2>

          <div className="space-y-4">
            <div className="bg-bg-main p-6 rounded-lg">
              <h3 className="text-lg font-medium text-text-primary mb-3">Retention Periods</h3>
              <ul className="text-text-primary space-y-2">
                <li>
                  <strong>Account Data:</strong> Retained while your account is active
                </li>
                <li>
                  <strong>Chat History:</strong> Retained according to your preferences (default: 1
                  year)
                </li>
                <li>
                  <strong>Usage Analytics:</strong> Aggregated data retained for 2 years
                </li>
                <li>
                  <strong>Legal Records:</strong> Retained as required by applicable law
                </li>
              </ul>
            </div>

            <div className="bg-yellow-50 bg-yellow-900/20 p-6 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <p className="text-yellow-900 dark:text-yellow-100">
                <strong>Account Deletion:</strong> When you delete your account, we remove your
                personal data within 30 days, except where retention is required by law or for
                legitimate business purposes.
              </p>
            </div>
          </div>
        </section>

        {/* Children's Privacy */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-text-primary mb-6">Children's Privacy</h2>

          <div className="bg-red-50 bg-red-900/20 p-6 rounded-lg border border-red-200 dark:border-red-800">
            <p className="text-red-900 dark:text-red-100 mb-4">
              Our services are not intended for children under 13 years of age. We do not knowingly
              collect personal information from children under 13.
            </p>
            <p className="text-red-800 dark:text-red-200">
              If you believe we have collected information from a child under 13, please contact us
              immediately so we can delete such information.
            </p>
          </div>
        </section>

        {/* Changes to Policy */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-text-primary mb-6">
            Changes to This Privacy Policy
          </h2>

          <div className="bg-bg-main p-6 rounded-lg">
            <p className="text-text-primary mb-4">
              We may update this Privacy Policy from time to time. When we make changes:
            </p>
            <ul className="list-disc list-inside text-text-primary space-y-2">
              <li>We will notify you via email or through our service</li>
              <li>We will update the "Last updated" date at the top of this policy</li>
              <li>Material changes will be highlighted and explained</li>
              <li>You will have the opportunity to review changes before they take effect</li>
            </ul>
          </div>
        </section>

        {/* Contact Information */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-text-primary mb-6">Contact Us</h2>

          <div className="bg-linear-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-8 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-text-primary mb-6">
              If you have any questions about this Privacy Policy or our privacy practices, please
              contact us:
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-text-primary mb-3">General Inquiries</h3>
                <p className="text-text-primary">
                  Email: privacy@pythra.com
                  <br />
                  Response time: Within 48 hours
                </p>
              </div>

              <div>
                <h3 className="font-medium text-text-primary mb-3">Data Protection Officer</h3>
                <p className="text-text-primary">
                  Email: dpo@pythra.com
                  <br />
                  For GDPR and data protection matters
                </p>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-blue-200 dark:border-blue-700">
              <p className="text-sm text-text-secondary">
                We are committed to resolving any privacy concerns promptly and transparently. Your
                privacy is important to us, and we appreciate your trust in our services.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
